import i18n from '@i18next/i18n';
import type { IBaseViewableServiceLayer } from '@src/slide/application/entry/toc/base/service-layer/base-viewable-service-layer.interface';
import type { IMobileEditorViewableServiceLayer } from '@src/slide/application/entry/toc/editor/mobile/service-layer/viewable-service-layer.interface';
import type { ISlideContentLayoutAnalyzerService } from '@src/slide/application/service/slide-content-layout-analyzer/core/slide-content-layout-analyzer-service.interface';
import type { IUserPriviledgeService } from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import type { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import ua from '@tencent/docs-user-agent';
import DUISnackbar from '@tencent/dui/lib/components/Snackbar';
import { getQuery } from 'sutils';
import { PPTEditorStateEnumType } from '../edit-extension/edit-extension-export';
import { ReportKey, type AIGenerator } from './ai-generator';
import { WritingContext } from './ai-generator/data-utils';
import type { IAIAssistantService } from './ai-ssistant-service.interface';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import { IChangeTemplateService } from '@src/slide/application/service/change-template-service/index.interface';
import { IPresentationData, ISlideDataModel } from '@tencent/slide-sdk-model';
import { reaction } from 'mobx';

import { getSlideTag } from '@tencent/slide-markdown-converter/src/utils';
import { SlideTag } from '@tencent/slide-markdown-converter/src/define';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';
import { reportUtils } from '../ai/core/api/report-utils';
import { setReportContext, clearReportContext } from '@src/slide/infrastructure/module/dependent/report/real-time-report/real-time';
import { ITemplateContentAnalyzerService } from '../template-content-analyzer-service/index.interface';
import {
    IPcEditorViewableServiceLayer,
} from '@src/slide/application/entry/toc/editor/pc/service-layer/viewable-service-layer.interface';

/** AI 助手服务 */
export class AIAssistantService implements IAIAssistantService {
    private aiGenerator: AIGenerator;
    /** 模版切换是否走板式 默认为美化 */
    private isLayoutTemplate: Boolean = false;
    /** 标记此次 commit 是否为AI造成 */
    private isCommitByAi: Boolean = false;
    /** initInstance函数中初始化的Promise结果 */
    private snackbarLoading: ReturnType<typeof DUISnackbar.show>;
    private userPriviledgeService: IUserPriviledgeService;
    /** 生成文档结束时的回调 */
    private slideGenerateFinishCallback?: (state: 'normal' | 'error') => void;
    /** 生成文档结束时的回调 */
    private isImageFirstGeneration: boolean;
    /** slideTag 缓存  */
    private slideTagCache?: SlideTag;
    /** 协同层 ready 才可以调用 api 事件 */
    private collaborationReadyPromise: Promise<void>;

    public constructor(
        private uiDataService: IUiDataService,
        private contentLayoutAnalyzer: ISlideContentLayoutAnalyzerService,
        private viewableServiceLayer: IBaseViewableServiceLayer,
        private templateContentAnalyzer: IDynamicService<ITemplateContentAnalyzerService>,
        private changeTemplateDynamicService?: IDynamicService<IChangeTemplateService>,
    ) {
        this.userPriviledgeService = this.viewableServiceLayer.userPriviledgeService;
        // 判定为 AI 文档, 尝试续写
        if (getQuery('openAiAssistant') === '1' || getQuery('AIGenerate') === '1') {
            eventBus.on(EventKeys.FIRST_PAGE_DISPLAY, () => {
                this.tryContinueWriting();
            });
            eventBus.on(EventKeys.RETRY_AI_WRITING, () => {
                this.tryContinueWriting(true);
            });
            this.collaborationReadyPromise = (() => {
                const report = reportUtils.start(ReportKey.AI_GENERATE_WAIT_FOR_READY);
                const failTimer = setTimeout(() => report.fail(), 10000);
                return new Promise<void>(resolve => OnlineEditServices.CollaborationManager
                    .promise().then(col => col.onCollaborationReady(() => {
                        clearTimeout(failTimer);
                        report.success();
                        resolve();
                    })));
            })();
        }
    }

    /**
     * 供AI模版切换调用的函数
     */
    public async applyTemplate(id: string) {
        await this.loadAIGenerator();
        const templateData = await this.aiGenerator.getTemplateDataById(id);
        this.markAiOperate();
        await this.applyTemplateByData(templateData, id);
        this.clearAiOperate();
    }

    public async extractSlideContent(): Promise<string> {
        await this.loadAIGenerator();
        return this.aiGenerator.extractSlideContentAsMarkdownText();
    }

    public async extractSlideContentAsJsonString(): Promise<string> {
        await this.loadAIGenerator();
        return this.aiGenerator.extractSlideContentAsJsonString();
    }

    /**
     * 插入幻灯片
     * @param content 简洁大纲 markdown 格式
     * @param templateId 模版id
     */
    public async insertSlides(context: WritingContext) {
        await this.loadAIGenerator();
        this.markAiOperate();
        await this._insertSlides(context);
        this.clearAiOperate();
    }

    /**
     * AI插入备注调用的函数
     * 应用 插入备注
     *
     * @param notesText 备注内容
     */
    public async insertSlideNotes(notesText: string) {
        await this.loadAIGenerator();
        this.markAiOperate();
        this.aiGenerator.insertSpeechTextIntoNotes(notesText);
        this.clearAiOperate();
    }

    /**
     * 给传进来的回调函数加锁，保证当前操作的commit不会改变模版切换的状态
     * 回调函数执行完毕后解锁
     */
    public async withLockAsync<T extends(...args: any[]) => Promise<any>>(cb: T): Promise<ReturnType<T>> {
        try {
            this.markAiOperate();
            return await cb();
        } finally {
            this.clearAiOperate();
        }
    }

    public withLock<T extends(...args: any[]) => any>(cb: T): ReturnType<T> {
        try {
            this.markAiOperate();
            return cb();
        } finally {
            this.clearAiOperate();
        }
    }

    public onSlideGenerateFinish(cb: (state: 'normal' | 'error') => void) {
        this.slideGenerateFinishCallback = cb;
    }

    /**
     * 从文档上下文判断是否是生图，非 localStorage
     */
    public get enableImageGeneration() {
        return this.isImageFirstGeneration;
    }

    /**
     * 重组 slide 页
     * @param slide
     * @param candidateCount 期望的候选数量，为 0 时返回所有候选。注意具体数目可能小于预期。
     * @returns IPresentationData
     */
    public async restructureSlide(slide: ISlideDataModel, candidateCount = 0): Promise<IPresentationData | void> {
        await this.loadAIGenerator();
        return this.aiGenerator.restructureSlide(slide, candidateCount, true);
    }

    public async restructureSlideDemo(): Promise<IPresentationData | void> {
        const { currentSlide } = this.viewableServiceLayer.selectionService;
        if (!currentSlide) return;

        const slide = currentSlide.sdkData as ISlideDataModel;
        const pData = await this.restructureSlide(slide, 0);
        if (!pData) return;

        const PresentationStoreService = SlideDataServices.SDKDataServices.PresentationStoreService();
        const pDataModel = PresentationStoreService?.createFromData(pData).getPresentation();

        (window as any).appServices.editableServiceLayer.pagingSelectionManager.updateData({
            title: '',
            presentationData: pDataModel,
            useCurrentSlideIndex: true,
        }).setVisible(true);
    }

    public async insertSlidePage(markdown: string): Promise<void> {
        await this.loadAIGenerator();

        this.aiGenerator.insertSlidePage(markdown);
    }

    /**
     * 根据模版数据切换模版 美化/板式
     *
     * @param templateData 模版数据
     */
    private async applyTemplateByData(templateData: IPresentationData, templateId: string) {
        if (this.isLayoutTemplate) {
            const presentationData = await this.aiGenerator.applyTemplate(templateData, templateId);
            if (presentationData) this.aiGenerator.replacePresentation(presentationData);
        } else {
            const changeTemplateService = await this.changeTemplateDynamicService?.promise();
            changeTemplateService?.changeTemplate(templateData);
        }
    }

    // TODO: 拆分文件
    private async _insertSlides(context: WritingContext, ignoreCoverPage = false): Promise<void> {
        await this.loadAIGenerator();
        this.snackbarLoading = DUISnackbar.show({
            message: i18n.t('AI文档助手创作中，请不要关闭页面'),
            type: 'loading',
            autoClose: false,
        });

        await this.collaborationReadyPromise;
        if (ua.isMobile) {
            return this.insertSlidesMobile(context, ignoreCoverPage);
        }
        return this.insertSlidesPC(context, ignoreCoverPage);
    }

    private async insertSlidesPC(context: WritingContext, ignoreCoverPage = false): Promise<void> {
        this.userPriviledgeService.setPPTEditorState({ state: PPTEditorStateEnumType.SELECTABLE });
        const disposeLock = this.userPriviledgeService.addStateWillChangeCallbacks(() => false);

        const removeUnloadListener = this.addUnloadListener();
        const removeCurrentSlideChangeListener = this.addCurrentSlideChangeListener();

        const onFinish = (state: 'normal' | 'error') => {
            disposeLock();
            this.userPriviledgeService.setPPTEditorState({ state: this.userPriviledgeService.fileAuth });

            removeUnloadListener();
            removeCurrentSlideChangeListener();

            this.snackbarLoading.close();
            this.slideGenerateFinishCallback?.(state);
        };

        try {
            await this.aiGenerator.insertSlides(
                context,
                ignoreCoverPage,
                ({ total, left }) => {
                    this.snackbarLoading.update({
                        message: `${i18n.t('正在全力为您生成配图')} (${total - left}/${total})`,
                    });
                },
            );
            onFinish('normal');
        } catch (e) {
            onFinish('error');
        }
    }

    private async insertSlidesMobile(context: WritingContext, ignoreCoverPage = false): Promise<void> {
        const { mobilePreviewThumbnailListService } = this.viewableServiceLayer as IMobileEditorViewableServiceLayer;
        // 隐藏移动端工具栏（顶部栏+底部胶囊）
        // 先切后锁
        mobilePreviewThumbnailListService.toggleToolbarDisabled(true);

        const dispose = reaction(() => mobilePreviewThumbnailListService.isListAtBottom, (isListAtBottom) => {
            this.aiGenerator.shouldFocusOnAddPage = isListAtBottom;
        });

        try {
            await this.aiGenerator.insertSlides(context, ignoreCoverPage);
        } finally {
            mobilePreviewThumbnailListService.toggleToolbarDisabled(false);
            dispose();
            this.snackbarLoading.close();
        }
    }

    /** 尝试续写 */
    private async tryContinueWriting(forceRetry?: boolean) {
        // 必须是文档所有者
        const { isOwner } = SlideDataServices.DocInfoManager() ?? {};
        if (!isOwner) return;

        const presentation = (
            await SlideDataServices.SDKDataServices.PresentationStoreService.promise()
        ).getPresentation();

        // 预期此时只该有一页内容
        if (presentation.slideList.length !== 1) return;

        const [firstPage] = presentation.slideList;
        let tags = getSlideTag(firstPage.getWritableOwnedData());

        setReportContext(['ai-writing']);

        if (!tags?.continueWritingContext && forceRetry && this.slideTagCache) {
            tags = { ...this.slideTagCache };
        }

        if (tags?.continueWritingContext) {
            this.slideTagCache = { ...tags };
            // 删除续写标记
            const { ...writingContext } = tags.continueWritingContext;
            let ignoreCoverPage = false;
            if (tags.isTemplate) {
                // 常规流程，已在 node 服务生成了封面页，此时只删除首页的续写上文，开始生成。
                ignoreCoverPage = true;

                delete tags.continueWritingContext;
                this.collaborationReadyPromise.then(() => {
                    firstPage.shapeTree?.api.updateProps({
                        nvPr: {
                            descr: JSON.stringify(tags),
                        },
                    });
                });
            } else {
                // 元宝场景，前置没有选择模板，使用了默认的空白模板 id 新建。
                // 需要用户选择模板才能继续新建流程。
                const { showTemplateModal } = (await import('./ai-generator/template-modal'));
                const res = await showTemplateModal();
                if (!res) return;

                const { templateId, enableImageGeneration } = res;

                writingContext.templateId = templateId;
                writingContext.enableImageGeneration = enableImageGeneration;
                this.collaborationReadyPromise.then(() => {
                    // 此场景下，首页是空白页面，需要删除。
                    presentation.api.removeSlides([firstPage]);
                });
            }

            // 设置场景标志
            this.isImageFirstGeneration = !!writingContext.enableImageGeneration;

            // AI创作完成后即可进行板式切换、开启commit监听
            this._insertSlides(writingContext, ignoreCoverPage).then(() => {
                this.isLayoutTemplate = true;
                this.addCommitChangeListener();

                clearReportContext();
            });
        }
    }

    private async loadAIGenerator() {
        const { AIGenerator } = await import('./ai-generator');

        if (this.aiGenerator) return this.aiGenerator;

        this.aiGenerator = new AIGenerator(
            this.uiDataService,
            this.contentLayoutAnalyzer,
            this.viewableServiceLayer.selectionService,
            this.templateContentAnalyzer,
            (this.viewableServiceLayer as IPcEditorViewableServiceLayer)?.slideListService,
        );
        return this.aiGenerator;
    }

    /**
     * 监听所有的commit，AI页面编辑触发的变更将使模版切换走美化
     */
    private addCommitChangeListener(): void {
        const eventManager = SlideDataServices.SDKDataServices.EventManager();
        if (!eventManager) return;

        const dispose = eventManager.commandEvents
            .onUndoRedoChange(async () => {
                if (!this.isCommitByAi) {
                    this.isLayoutTemplate = false;
                    dispose();
                }
            });
    }

    /**
     * 标记通过ai编辑页面造成的commit
     */
    private markAiOperate(): void {
        this.isCommitByAi = true;
    }

    /**
     * 清除通过ai编辑页面造成的commit标记
     */
    private clearAiOperate() {
        this.isCommitByAi = false;
    }

    private addUnloadListener() {
        function onBeforeUnload(event: BeforeUnloadEvent) {
            const message = i18n.t('你确定要离开此页面吗？未保存的更改将会丢失。');
            event.returnValue = message;
            DUISnackbar.show({
                type: 'warning',
                message: i18n.t('退出后图片无法继续生成'),
                autoClose: true,
                duration: 2000,
            });
            return message;
        }

        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }

    private addCurrentSlideChangeListener() {
        const onChange = () => {
            // 初始默认在添加页时选中
            // 用户手动选中其他页时，脱离跟随状态；直到用户选中最后一页
            const atLastPage = this.uiDataService.slideModelList.length - 1
                === this.viewableServiceLayer.selectionService.currentSlideIndex;

            this.aiGenerator.shouldFocusOnAddPage = atLastPage;
        };

        window.addEventListener('onCurrentSlideIndexChangeByUserAction', onChange);
        return () => window.removeEventListener('onCurrentSlideIndexChangeByUserAction', onChange);
    }
}

